import React, { useState } from 'react';
import { motion, AnimatePresence } from 'framer-motion';
import SafeIcon from '../common/SafeIcon';
import * as FiIcons from 'react-icons/fi';

const { FiTrendingUp, FiUsers, FiFileText, FiDollarSign, FiEye, FiSend, FiClock, FiX, FiPlus, FiTrash2, FiCheck, FiXCircle } = FiIcons;

// Available customers for selection
const AVAILABLE_CUSTOMERS = [
  { id: 1, name: 'Fresh Market Retailers' },
  { id: 2, name: 'City Supermarket Chain' },
  { id: 3, name: 'Organic Food Co' },
  { id: 4, name: 'Green Grocers Ltd' },
  { id: 5, name: 'Farm Fresh Stores' }
];

// Available products for selection
const AVAILABLE_PRODUCTS = [
  { id: 1, sku: 'APL-001', name: 'Granny Smith Apples', unitPrice: 15.50 },
  { id: 2, sku: 'ORG-023', name: 'Navel Oranges', unitPrice: 18.00 },
  { id: 3, sku: 'BAN-012', name: 'Cavendish Bananas', unitPrice: 12.00 },
  { id: 4, sku: 'TOM-045', name: 'Beef Tomatoes', unitPrice: 25.00 },
  { id: 5, sku: 'LET-078', name: 'Iceberg Lettuce', unitPrice: 8.50 },
  { id: 6, sku: 'HRB-101', name: 'Mixed Herbs', unitPrice: 35.00 },
  { id: 7, sku: 'TOM-046', name: 'Cherry Tomatoes', unitPrice: 28.00 }
];

// Dark theme status styles
const STATUS_COLORS = {
  sent: 'bg-sky-500/10 text-sky-400 border-sky-500/30',
  approved: 'bg-lime-500/10 text-lime-400 border-lime-500/30',
  draft: 'bg-slate-500/10 text-slate-400 border-slate-500/30',
  rejected: 'bg-red-500/10 text-red-400 border-red-500/30',
  active: 'bg-lime-500/10 text-lime-400 border-lime-500/30',
  premium: 'bg-violet-500/10 text-violet-400 border-violet-500/30',
  at_risk: 'bg-red-500/10 text-red-400 border-red-500/30'
};

// Initial pipeline data
const INITIAL_PIPELINE_DATA = [
  {
    id: 1,
    proposalId: 'PROP-2024-001',
    customer: 'Fresh Market Retailers',
    items: ['Granny Smith Apples', 'Navel Oranges'],
    totalValue: 45750,
    status: 'sent',
    createdDate: '2024-01-15',
    expiryDate: '2024-01-22'
  },
  {
    id: 2,
    proposalId: 'PROP-2024-002',
    customer: 'City Supermarket Chain',
    items: ['Beef Tomatoes', 'Iceberg Lettuce', 'Cavendish Bananas'],
    totalValue: 78900,
    status: 'approved',
    createdDate: '2024-01-14',
    expiryDate: '2024-01-21'
  },
  {
    id: 3,
    proposalId: 'PROP-2024-003',
    customer: 'Organic Food Co',
    items: ['Mixed Herbs', 'Cherry Tomatoes'],
    totalValue: 23400,
    status: 'draft',
    createdDate: '2024-01-15',
    expiryDate: '2024-01-25'
  }
];

// Load from localStorage or use initial data
const loadPipelineData = () => {
  try {
    const saved = localStorage.getItem('fresh-board-proposals');
    return saved ? JSON.parse(saved) : INITIAL_PIPELINE_DATA;
  } catch {
    return INITIAL_PIPELINE_DATA;
  }
};

const loadProposalCounter = () => {
  try {
    const saved = localStorage.getItem('fresh-board-proposal-counter');
    return saved ? parseInt(saved, 10) : 4;
  } catch {
    return 4;
  }
};

const Sales = () => {
  const [activeTab, setActiveTab] = useState('pipeline');
  const [isModalOpen, setIsModalOpen] = useState(false);
  const [proposalCounter, setProposalCounter] = useState(loadProposalCounter);

  // Form state for new proposal
  const [selectedCustomer, setSelectedCustomer] = useState('');
  const [selectedItems, setSelectedItems] = useState([]);
  const [expirationDate, setExpirationDate] = useState('');

  // State for proposal detail/approve modal
  const [viewingProposal, setViewingProposal] = useState(null);

  const [pipelineData, setPipelineData] = useState(loadPipelineData);

  // Save to localStorage whenever pipelineData changes
  React.useEffect(() => {
    localStorage.setItem('fresh-board-proposals', JSON.stringify(pipelineData));
  }, [pipelineData]);

  // Save proposal counter to localStorage
  React.useEffect(() => {
    localStorage.setItem('fresh-board-proposal-counter', String(proposalCounter));
  }, [proposalCounter]);

  // State for viewing customer
  const [viewingCustomer, setViewingCustomer] = useState(null);

  const customerData = [
    {
      id: 1,
      name: 'Fresh Market Retailers',
      loyaltyPoints: 2450,
      lastOrder: '2024-01-10',
      totalSpent: 156780,
      status: 'active',
      followUpDate: '2024-01-18',
      orderHistory: [
        { id: 'ORD-2024-045', date: '2024-01-10', items: ['Granny Smith Apples', 'Navel Oranges'], total: 23450, status: 'delivered' },
        { id: 'ORD-2024-038', date: '2024-01-03', items: ['Beef Tomatoes', 'Iceberg Lettuce'], total: 18900, status: 'delivered' },
        { id: 'ORD-2023-412', date: '2023-12-28', items: ['Mixed Herbs', 'Cherry Tomatoes', 'Cavendish Bananas'], total: 34560, status: 'delivered' }
      ]
    },
    {
      id: 2,
      name: 'City Supermarket Chain',
      loyaltyPoints: 5670,
      lastOrder: '2024-01-14',
      totalSpent: 445600,
      status: 'premium',
      followUpDate: '2024-01-20',
      orderHistory: [
        { id: 'ORD-2024-052', date: '2024-01-14', items: ['Beef Tomatoes', 'Iceberg Lettuce', 'Cavendish Bananas'], total: 78900, status: 'delivered' },
        { id: 'ORD-2024-041', date: '2024-01-07', items: ['Granny Smith Apples', 'Navel Oranges', 'Mixed Herbs'], total: 56700, status: 'delivered' },
        { id: 'ORD-2024-029', date: '2023-12-31', items: ['Cherry Tomatoes', 'Cavendish Bananas'], total: 42300, status: 'delivered' },
        { id: 'ORD-2023-398', date: '2023-12-24', items: ['Granny Smith Apples', 'Beef Tomatoes'], total: 67800, status: 'delivered' }
      ]
    },
    {
      id: 3,
      name: 'Organic Food Co',
      loyaltyPoints: 890,
      lastOrder: '2024-01-05',
      totalSpent: 67890,
      status: 'at_risk',
      followUpDate: '2024-01-16',
      orderHistory: [
        { id: 'ORD-2024-033', date: '2024-01-05', items: ['Mixed Herbs', 'Cherry Tomatoes'], total: 23400, status: 'delivered' },
        { id: 'ORD-2023-385', date: '2023-12-18', items: ['Iceberg Lettuce', 'Navel Oranges'], total: 44490, status: 'delivered' }
      ]
    }
  ];

  const getStatusColor = (status) => {
    return STATUS_COLORS[status] || 'bg-slate-500/10 text-slate-400 border-slate-500/30';
  };

  const salesMetrics = {
    conversionRate: 68.5,
    quoteConversion: 45.2,
    totalProposals: 156 + pipelineData.length - 3,
    avgOrderValue: 34500
  };

  const addItem = () => {
    setSelectedItems([...selectedItems, { productId: '', quantity: 1 }]);
  };

  const removeItem = (index) => {
    setSelectedItems(selectedItems.filter((_, i) => i !== index));
  };

  const updateItem = (index, field, value) => {
    const updated = [...selectedItems];
    updated[index] = { ...updated[index], [field]: value };
    setSelectedItems(updated);
  };

  const calculateTotal = () => {
    return selectedItems.reduce((total, item) => {
      const product = AVAILABLE_PRODUCTS.find(p => p.id === parseInt(item.productId));
      if (product) {
        return total + (product.unitPrice * item.quantity);
      }
      return total;
    }, 0);
  };

  const resetForm = () => {
    setSelectedCustomer('');
    setSelectedItems([]);
    setExpirationDate('');
  };

  const handleSubmit = (e) => {
    e.preventDefault();

    if (!selectedCustomer || selectedItems.length === 0 || !expirationDate) {
      return;
    }

    const itemNames = selectedItems
      .map(item => {
        const product = AVAILABLE_PRODUCTS.find(p => p.id === parseInt(item.productId));
        return product ? product.name : '';
      })
      .filter(name => name);

    const today = new Date();
    const createdDate = today.toISOString().split('T')[0];

    const newProposal = {
      id: Date.now(),
      proposalId: `PROP-2024-${String(proposalCounter).padStart(3, '0')}`,
      customer: selectedCustomer,
      items: itemNames,
      totalValue: calculateTotal(),
      status: 'draft',
      createdDate: createdDate,
      expiryDate: expirationDate
    };

    setPipelineData([newProposal, ...pipelineData]);
    setProposalCounter(proposalCounter + 1);
    resetForm();
    setIsModalOpen(false);
  };

  const getDefaultExpirationDate = () => {
    const date = new Date();
    date.setDate(date.getDate() + 7);
    return date.toISOString().split('T')[0];
  };

  const updateProposalStatus = (proposalId, newStatus) => {
    setPipelineData(pipelineData.map(proposal =>
      proposal.id === proposalId
        ? { ...proposal, status: newStatus }
        : proposal
    ));
  };

  const sendProposal = (proposalId) => {
    updateProposalStatus(proposalId, 'sent');
  };

  const approveProposal = (proposalId) => {
    updateProposalStatus(proposalId, 'approved');
    setViewingProposal(null);
  };

  const rejectProposal = (proposalId) => {
    updateProposalStatus(proposalId, 'rejected');
    setViewingProposal(null);
  };

  return (
    <div className="space-y-6">
      {/* Header */}
      <div className="flex items-center justify-between">
        <div>
          <h1 className="font-display text-3xl font-semibold text-[#e8f5e9] tracking-tight">
            <span className="text-[#84cc16]">Sales</span> Pipeline
          </h1>
          <p className="text-[#6b8772] mt-1 font-mono text-sm">Proposal management and customer relationships</p>
        </div>
        <motion.button
          whileHover={{ scale: 1.02 }}
          whileTap={{ scale: 0.98 }}
          onClick={() => {
            resetForm();
            setExpirationDate(getDefaultExpirationDate());
            setIsModalOpen(true);
          }}
          className="bg-gradient-to-r from-lime-500 to-lime-600 text-[#050807] px-4 py-2 rounded-lg shadow-lg shadow-lime-500/20 hover:shadow-lime-500/30 transition-shadow flex items-center gap-2 font-mono text-sm font-medium"
        >
          <SafeIcon icon={FiFileText} className="w-4 h-4" />
          <span>New Proposal</span>
        </motion.button>
      </div>

      {/* Metrics Grid */}
      <div className="grid grid-cols-1 md:grid-cols-4 gap-4">
        <motion.div
          initial={{ opacity: 0, y: 16 }}
          animate={{ opacity: 1, y: 0 }}
          className="bg-[#111916] rounded-xl p-5 border border-[#243129] hover:border-lime-500/30 transition-colors"
        >
          <div className="flex items-center justify-between">
            <div>
              <p className="text-xs font-mono text-[#6b8772] uppercase tracking-wider">Lead Conversion</p>
              <p className="text-2xl font-display font-semibold text-lime-400 mt-1">{salesMetrics.conversionRate}%</p>
            </div>
            <div className="bg-lime-500/10 rounded-xl p-3 border border-lime-500/20">
              <SafeIcon icon={FiTrendingUp} className="w-5 h-5 text-lime-400" />
            </div>
          </div>
        </motion.div>

        <motion.div
          initial={{ opacity: 0, y: 16 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ delay: 0.05 }}
          className="bg-[#111916] rounded-xl p-5 border border-[#243129] hover:border-sky-500/30 transition-colors"
        >
          <div className="flex items-center justify-between">
            <div>
              <p className="text-xs font-mono text-[#6b8772] uppercase tracking-wider">Quote→Order Rate</p>
              <p className="text-2xl font-display font-semibold text-sky-400 mt-1">{salesMetrics.quoteConversion}%</p>
            </div>
            <div className="bg-sky-500/10 rounded-xl p-3 border border-sky-500/20">
              <SafeIcon icon={FiFileText} className="w-5 h-5 text-sky-400" />
            </div>
          </div>
        </motion.div>

        <motion.div
          initial={{ opacity: 0, y: 16 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ delay: 0.1 }}
          className="bg-[#111916] rounded-xl p-5 border border-[#243129] hover:border-violet-500/30 transition-colors"
        >
          <div className="flex items-center justify-between">
            <div>
              <p className="text-xs font-mono text-[#6b8772] uppercase tracking-wider">Total Proposals</p>
              <p className="text-2xl font-display font-semibold text-violet-400 mt-1">{salesMetrics.totalProposals}</p>
            </div>
            <div className="bg-violet-500/10 rounded-xl p-3 border border-violet-500/20">
              <SafeIcon icon={FiUsers} className="w-5 h-5 text-violet-400" />
            </div>
          </div>
        </motion.div>

        <motion.div
          initial={{ opacity: 0, y: 16 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ delay: 0.15 }}
          className="bg-[#111916] rounded-xl p-5 border border-[#243129] hover:border-amber-500/30 transition-colors"
        >
          <div className="flex items-center justify-between">
            <div>
              <p className="text-xs font-mono text-[#6b8772] uppercase tracking-wider">Avg Order Value</p>
              <p className="text-2xl font-display font-semibold text-amber-400 mt-1">N$ {salesMetrics.avgOrderValue.toLocaleString()}</p>
            </div>
            <div className="bg-amber-500/10 rounded-xl p-3 border border-amber-500/20">
              <SafeIcon icon={FiDollarSign} className="w-5 h-5 text-amber-400" />
            </div>
          </div>
        </motion.div>
      </div>

      {/* Tabs */}
      <div className="bg-[#111916] rounded-xl border border-[#243129]">
        <div className="border-b border-[#243129]">
          <nav className="flex gap-8 px-6">
            {[
              { id: 'pipeline', label: 'Proposal Pipeline', count: pipelineData.length },
              { id: 'customers', label: 'Customer Loyalty', count: customerData.length }
            ].map((tab) => (
              <button
                key={tab.id}
                onClick={() => setActiveTab(tab.id)}
                className={`flex items-center gap-2 py-4 border-b-2 transition-colors font-mono text-sm ${
                  activeTab === tab.id
                    ? 'border-[#84cc16] text-[#84cc16]'
                    : 'border-transparent text-[#6b8772] hover:text-[#a5c4ad]'
                }`}
              >
                <span className="font-medium">{tab.label}</span>
                <span className={`px-2 py-0.5 text-xs rounded-full ${
                  activeTab === tab.id ? 'bg-lime-500/10 text-lime-400' : 'bg-[#1c2822] text-[#6b8772]'
                }`}>
                  {tab.count}
                </span>
              </button>
            ))}
          </nav>
        </div>

        <div className="p-5">
          {activeTab === 'pipeline' && (
            <div className="space-y-3">
              {pipelineData.map((proposal, index) => (
                <motion.div
                  key={proposal.id}
                  initial={{ opacity: 0, y: 10 }}
                  animate={{ opacity: 1, y: 0 }}
                  transition={{ delay: index * 0.05 }}
                  className="bg-[#0a0f0d] border border-[#1c2822] rounded-lg p-4 hover:border-[#243129] transition-colors group"
                >
                  <div className="flex items-center justify-between mb-3">
                    <div className="flex items-center gap-3">
                      <span className={`px-2 py-0.5 text-[10px] font-mono font-medium rounded border uppercase tracking-wider ${getStatusColor(proposal.status)}`}>
                        {proposal.status}
                      </span>
                      <div>
                        <h4 className="font-mono font-medium text-[#e8f5e9] group-hover:text-[#84cc16] transition-colors">{proposal.proposalId}</h4>
                        <p className="text-sm font-mono text-[#6b8772]">{proposal.customer}</p>
                      </div>
                    </div>
                    <div className="text-right">
                      <p className="text-lg font-display font-semibold text-[#e8f5e9]">N$ {proposal.totalValue.toLocaleString()}</p>
                      <p className="text-xs font-mono text-[#6b8772]">Expires: {proposal.expiryDate}</p>
                    </div>
                  </div>

                  <div className="flex items-center justify-between">
                    <div>
                      <p className="text-sm font-mono text-[#6b8772]">Items: {proposal.items.join(', ')}</p>
                      <p className="text-xs font-mono text-[#4a5f50]">Created: {proposal.createdDate}</p>
                    </div>
                    <div className="flex items-center gap-2">
                      <button
                        onClick={() => setViewingProposal(proposal)}
                        className="text-[#6b8772] hover:text-[#84cc16] p-1.5 hover:bg-lime-500/10 rounded-md transition-colors"
                        title="View Details"
                      >
                        <SafeIcon icon={FiEye} className="w-4 h-4" />
                      </button>
                      {proposal.status === 'draft' && (
                        <button
                          onClick={() => sendProposal(proposal.id)}
                          className="text-[#6b8772] hover:text-sky-400 p-1.5 hover:bg-sky-500/10 rounded-md transition-colors"
                          title="Send Proposal"
                        >
                          <SafeIcon icon={FiSend} className="w-4 h-4" />
                        </button>
                      )}
                      {proposal.status === 'sent' && (
                        <>
                          <button
                            onClick={() => approveProposal(proposal.id)}
                            className="text-[#6b8772] hover:text-lime-400 p-1.5 hover:bg-lime-500/10 rounded-md transition-colors"
                            title="Approve"
                          >
                            <SafeIcon icon={FiCheck} className="w-4 h-4" />
                          </button>
                          <button
                            onClick={() => rejectProposal(proposal.id)}
                            className="text-[#6b8772] hover:text-red-400 p-1.5 hover:bg-red-500/10 rounded-md transition-colors"
                            title="Reject"
                          >
                            <SafeIcon icon={FiXCircle} className="w-4 h-4" />
                          </button>
                        </>
                      )}
                    </div>
                  </div>
                </motion.div>
              ))}
            </div>
          )}

          {activeTab === 'customers' && (
            <div className="space-y-3">
              {customerData.map((customer, index) => (
                <motion.div
                  key={customer.id}
                  initial={{ opacity: 0, y: 10 }}
                  animate={{ opacity: 1, y: 0 }}
                  transition={{ delay: index * 0.05 }}
                  className="bg-[#0a0f0d] border border-[#1c2822] rounded-lg p-4 hover:border-[#243129] transition-colors group cursor-pointer"
                  onClick={() => setViewingCustomer(customer)}
                >
                  <div className="flex items-center justify-between mb-3">
                    <div className="flex items-center gap-3">
                      <span className={`px-2 py-0.5 text-[10px] font-mono font-medium rounded border uppercase tracking-wider ${getStatusColor(customer.status)}`}>
                        {customer.status.replace('_', ' ')}
                      </span>
                      <div>
                        <h4 className="font-mono font-medium text-[#e8f5e9] group-hover:text-[#84cc16] transition-colors">{customer.name}</h4>
                        <p className="text-sm font-mono text-[#6b8772]">Last Order: {customer.lastOrder}</p>
                      </div>
                    </div>
                    <div className="text-right">
                      <p className="text-lg font-display font-semibold text-[#e8f5e9]">N$ {customer.totalSpent.toLocaleString()}</p>
                      <p className="text-sm font-mono text-lime-400">{customer.loyaltyPoints} points</p>
                    </div>
                  </div>

                  <div className="flex items-center justify-between">
                    <div className="flex items-center gap-4 text-sm font-mono text-[#6b8772]">
                      <div className="flex items-center gap-1">
                        <SafeIcon icon={FiClock} className="w-4 h-4" />
                        <span>Follow-up: {customer.followUpDate}</span>
                      </div>
                    </div>
                    <div className="flex items-center gap-3">
                      <button
                        onClick={(e) => { e.stopPropagation(); setViewingCustomer(customer); }}
                        className="text-[#6b8772] hover:text-[#84cc16] p-1.5 hover:bg-lime-500/10 rounded-md transition-colors"
                        title="View Order History"
                      >
                        <SafeIcon icon={FiEye} className="w-4 h-4" />
                      </button>
                      <button
                        onClick={(e) => e.stopPropagation()}
                        className="text-[#84cc16] hover:text-lime-300 text-sm font-mono font-medium transition-colors"
                      >
                        Send Follow-up
                      </button>
                    </div>
                  </div>
                </motion.div>
              ))}
            </div>
          )}
        </div>
      </div>

      {/* New Proposal Modal */}
      <AnimatePresence>
        {isModalOpen && (
          <motion.div
            initial={{ opacity: 0 }}
            animate={{ opacity: 1 }}
            exit={{ opacity: 0 }}
            className="fixed inset-0 bg-black/60 backdrop-blur-sm flex items-center justify-center z-50 p-4"
            onClick={() => setIsModalOpen(false)}
          >
            <motion.div
              initial={{ opacity: 0, scale: 0.95, y: 20 }}
              animate={{ opacity: 1, scale: 1, y: 0 }}
              exit={{ opacity: 0, scale: 0.95, y: 20 }}
              className="bg-[#111916] border border-[#243129] rounded-xl w-full max-w-2xl max-h-[90vh] overflow-y-auto"
              onClick={(e) => e.stopPropagation()}
            >
              {/* Modal Header */}
              <div className="flex items-center justify-between p-5 border-b border-[#243129]">
                <div>
                  <h2 className="text-xl font-display font-semibold text-[#e8f5e9]">Create New Proposal</h2>
                  <p className="text-sm font-mono text-[#6b8772] mt-1">Fill in the details to create a new proposal</p>
                </div>
                <button
                  onClick={() => setIsModalOpen(false)}
                  className="text-[#6b8772] hover:text-[#e8f5e9] p-2 hover:bg-[#1c2822] rounded-lg transition-colors"
                >
                  <SafeIcon icon={FiX} className="w-5 h-5" />
                </button>
              </div>

              {/* Modal Body */}
              <form onSubmit={handleSubmit} className="p-5 space-y-5">
                {/* Customer Selection */}
                <div>
                  <label className="block text-sm font-mono font-medium text-[#a5c4ad] mb-2">
                    Customer <span className="text-red-400">*</span>
                  </label>
                  <select
                    value={selectedCustomer}
                    onChange={(e) => setSelectedCustomer(e.target.value)}
                    className="w-full bg-[#0a0f0d] border border-[#243129] rounded-lg px-4 py-3 text-[#e8f5e9] font-mono text-sm focus:outline-none focus:border-[#84cc16] focus:ring-1 focus:ring-[rgba(132,204,22,0.2)] transition-all"
                    required
                  >
                    <option value="">Select a customer...</option>
                    {AVAILABLE_CUSTOMERS.map((customer) => (
                      <option key={customer.id} value={customer.name}>
                        {customer.name}
                      </option>
                    ))}
                  </select>
                </div>

                {/* Items Section */}
                <div>
                  <div className="flex items-center justify-between mb-2">
                    <label className="block text-sm font-mono font-medium text-[#a5c4ad]">
                      Items <span className="text-red-400">*</span>
                    </label>
                    <button
                      type="button"
                      onClick={addItem}
                      className="text-[#84cc16] hover:text-lime-300 text-sm font-mono font-medium flex items-center gap-1 transition-colors"
                    >
                      <SafeIcon icon={FiPlus} className="w-4 h-4" />
                      Add Item
                    </button>
                  </div>

                  {selectedItems.length === 0 ? (
                    <div className="bg-[#0a0f0d] border border-dashed border-[#243129] rounded-lg p-6 text-center">
                      <p className="text-[#6b8772] font-mono text-sm">No items added yet. Click "Add Item" to add products.</p>
                    </div>
                  ) : (
                    <div className="space-y-3">
                      {selectedItems.map((item, index) => (
                        <div key={index} className="flex items-center gap-3 bg-[#0a0f0d] border border-[#1c2822] rounded-lg p-3">
                          <select
                            value={item.productId}
                            onChange={(e) => updateItem(index, 'productId', e.target.value)}
                            className="flex-1 bg-[#111916] border border-[#243129] rounded-lg px-3 py-2 text-[#e8f5e9] font-mono text-sm focus:outline-none focus:border-[#84cc16] transition-all"
                            required
                          >
                            <option value="">Select product...</option>
                            {AVAILABLE_PRODUCTS.map((product) => (
                              <option key={product.id} value={product.id}>
                                {product.name} (N$ {product.unitPrice.toFixed(2)})
                              </option>
                            ))}
                          </select>
                          <div className="flex items-center gap-2">
                            <label className="text-xs font-mono text-[#6b8772]">Qty:</label>
                            <input
                              type="number"
                              min="1"
                              value={item.quantity}
                              onChange={(e) => updateItem(index, 'quantity', parseInt(e.target.value) || 1)}
                              className="w-20 bg-[#111916] border border-[#243129] rounded-lg px-3 py-2 text-[#e8f5e9] font-mono text-sm focus:outline-none focus:border-[#84cc16] transition-all"
                              required
                            />
                          </div>
                          <button
                            type="button"
                            onClick={() => removeItem(index)}
                            className="text-red-400 hover:text-red-300 p-2 hover:bg-red-500/10 rounded-lg transition-colors"
                          >
                            <SafeIcon icon={FiTrash2} className="w-4 h-4" />
                          </button>
                        </div>
                      ))}
                    </div>
                  )}
                </div>

                {/* Expiration Date */}
                <div>
                  <label className="block text-sm font-mono font-medium text-[#a5c4ad] mb-2">
                    Expiration Date <span className="text-red-400">*</span>
                  </label>
                  <input
                    type="date"
                    value={expirationDate}
                    onChange={(e) => setExpirationDate(e.target.value)}
                    className="w-full bg-[#0a0f0d] border border-[#243129] rounded-lg px-4 py-3 text-[#e8f5e9] font-mono text-sm focus:outline-none focus:border-[#84cc16] focus:ring-1 focus:ring-[rgba(132,204,22,0.2)] transition-all"
                    required
                  />
                </div>

                {/* Total Display */}
                {selectedItems.length > 0 && (
                  <div className="bg-[#0a0f0d] border border-[#243129] rounded-lg p-4">
                    <div className="flex items-center justify-between">
                      <span className="text-sm font-mono text-[#6b8772]">Estimated Total:</span>
                      <span className="text-xl font-display font-semibold text-lime-400">
                        N$ {calculateTotal().toLocaleString()}
                      </span>
                    </div>
                  </div>
                )}

                {/* Actions */}
                <div className="flex items-center gap-3 pt-2">
                  <button
                    type="button"
                    onClick={() => setIsModalOpen(false)}
                    className="flex-1 bg-[#1c2822] border border-[#243129] text-[#a5c4ad] px-4 py-3 rounded-lg hover:border-[#84cc16]/30 transition-colors font-mono text-sm font-medium"
                  >
                    Cancel
                  </button>
                  <button
                    type="submit"
                    disabled={!selectedCustomer || selectedItems.length === 0 || !expirationDate}
                    className="flex-1 bg-gradient-to-r from-lime-500 to-lime-600 text-[#050807] px-4 py-3 rounded-lg shadow-lg shadow-lime-500/20 hover:shadow-lime-500/30 transition-shadow font-mono text-sm font-medium disabled:opacity-50 disabled:cursor-not-allowed"
                  >
                    Create Proposal
                  </button>
                </div>
              </form>
            </motion.div>
          </motion.div>
        )}
      </AnimatePresence>

      {/* View/Approve Proposal Modal */}
      <AnimatePresence>
        {viewingProposal && (
          <motion.div
            initial={{ opacity: 0 }}
            animate={{ opacity: 1 }}
            exit={{ opacity: 0 }}
            className="fixed inset-0 bg-black/60 backdrop-blur-sm flex items-center justify-center z-50 p-4"
            onClick={() => setViewingProposal(null)}
          >
            <motion.div
              initial={{ opacity: 0, scale: 0.95, y: 20 }}
              animate={{ opacity: 1, scale: 1, y: 0 }}
              exit={{ opacity: 0, scale: 0.95, y: 20 }}
              className="bg-[#111916] border border-[#243129] rounded-xl w-full max-w-lg"
              onClick={(e) => e.stopPropagation()}
            >
              {/* Modal Header */}
              <div className="flex items-center justify-between p-5 border-b border-[#243129]">
                <div>
                  <h2 className="text-xl font-display font-semibold text-[#e8f5e9]">{viewingProposal.proposalId}</h2>
                  <p className="text-sm font-mono text-[#6b8772] mt-1">{viewingProposal.customer}</p>
                </div>
                <button
                  onClick={() => setViewingProposal(null)}
                  className="text-[#6b8772] hover:text-[#e8f5e9] p-2 hover:bg-[#1c2822] rounded-lg transition-colors"
                >
                  <SafeIcon icon={FiX} className="w-5 h-5" />
                </button>
              </div>

              {/* Modal Body */}
              <div className="p-5 space-y-4">
                <div className="flex items-center justify-between">
                  <span className="text-sm font-mono text-[#6b8772]">Status:</span>
                  <span className={`px-2 py-0.5 text-[10px] font-mono font-medium rounded border uppercase tracking-wider ${getStatusColor(viewingProposal.status)}`}>
                    {viewingProposal.status}
                  </span>
                </div>
                <div className="flex items-center justify-between">
                  <span className="text-sm font-mono text-[#6b8772]">Total Value:</span>
                  <span className="text-lg font-display font-semibold text-lime-400">N$ {viewingProposal.totalValue.toLocaleString()}</span>
                </div>
                <div>
                  <span className="text-sm font-mono text-[#6b8772]">Items:</span>
                  <ul className="mt-2 space-y-1">
                    {viewingProposal.items.map((item, idx) => (
                      <li key={idx} className="text-sm font-mono text-[#e8f5e9] flex items-center gap-2">
                        <span className="w-1.5 h-1.5 rounded-full bg-lime-400"></span>
                        {item}
                      </li>
                    ))}
                  </ul>
                </div>
                <div className="flex items-center justify-between">
                  <span className="text-sm font-mono text-[#6b8772]">Created:</span>
                  <span className="text-sm font-mono text-[#e8f5e9]">{viewingProposal.createdDate}</span>
                </div>
                <div className="flex items-center justify-between">
                  <span className="text-sm font-mono text-[#6b8772]">Expires:</span>
                  <span className="text-sm font-mono text-[#e8f5e9]">{viewingProposal.expiryDate}</span>
                </div>
              </div>

              {/* Modal Actions */}
              <div className="p-5 border-t border-[#243129]">
                {viewingProposal.status === 'draft' && (
                  <div className="flex items-center gap-3">
                    <button
                      onClick={() => setViewingProposal(null)}
                      className="flex-1 bg-[#1c2822] border border-[#243129] text-[#a5c4ad] px-4 py-3 rounded-lg hover:border-[#84cc16]/30 transition-colors font-mono text-sm font-medium"
                    >
                      Close
                    </button>
                    <button
                      onClick={() => {
                        sendProposal(viewingProposal.id);
                        setViewingProposal({ ...viewingProposal, status: 'sent' });
                      }}
                      className="flex-1 bg-gradient-to-r from-sky-500 to-sky-600 text-white px-4 py-3 rounded-lg shadow-lg shadow-sky-500/20 hover:shadow-sky-500/30 transition-shadow font-mono text-sm font-medium flex items-center justify-center gap-2"
                    >
                      <SafeIcon icon={FiSend} className="w-4 h-4" />
                      Send Proposal
                    </button>
                  </div>
                )}
                {viewingProposal.status === 'sent' && (
                  <div className="flex items-center gap-3">
                    <button
                      onClick={() => rejectProposal(viewingProposal.id)}
                      className="flex-1 bg-red-500/10 border border-red-500/30 text-red-400 px-4 py-3 rounded-lg hover:bg-red-500/20 transition-colors font-mono text-sm font-medium flex items-center justify-center gap-2"
                    >
                      <SafeIcon icon={FiXCircle} className="w-4 h-4" />
                      Reject
                    </button>
                    <button
                      onClick={() => approveProposal(viewingProposal.id)}
                      className="flex-1 bg-gradient-to-r from-lime-500 to-lime-600 text-[#050807] px-4 py-3 rounded-lg shadow-lg shadow-lime-500/20 hover:shadow-lime-500/30 transition-shadow font-mono text-sm font-medium flex items-center justify-center gap-2"
                    >
                      <SafeIcon icon={FiCheck} className="w-4 h-4" />
                      Approve
                    </button>
                  </div>
                )}
                {(viewingProposal.status === 'approved' || viewingProposal.status === 'rejected') && (
                  <button
                    onClick={() => setViewingProposal(null)}
                    className="w-full bg-[#1c2822] border border-[#243129] text-[#a5c4ad] px-4 py-3 rounded-lg hover:border-[#84cc16]/30 transition-colors font-mono text-sm font-medium"
                  >
                    Close
                  </button>
                )}
              </div>
            </motion.div>
          </motion.div>
        )}
      </AnimatePresence>

      {/* Customer Order History Modal */}
      <AnimatePresence>
        {viewingCustomer && (
          <motion.div
            initial={{ opacity: 0 }}
            animate={{ opacity: 1 }}
            exit={{ opacity: 0 }}
            className="fixed inset-0 bg-black/60 backdrop-blur-sm flex items-center justify-center z-50 p-4"
            onClick={() => setViewingCustomer(null)}
          >
            <motion.div
              initial={{ opacity: 0, scale: 0.95, y: 20 }}
              animate={{ opacity: 1, scale: 1, y: 0 }}
              exit={{ opacity: 0, scale: 0.95, y: 20 }}
              className="bg-[#111916] border border-[#243129] rounded-xl w-full max-w-2xl max-h-[90vh] overflow-y-auto"
              onClick={(e) => e.stopPropagation()}
            >
              {/* Modal Header */}
              <div className="flex items-center justify-between p-5 border-b border-[#243129]">
                <div>
                  <h2 className="text-xl font-display font-semibold text-[#e8f5e9]">{viewingCustomer.name}</h2>
                  <div className="flex items-center gap-2 mt-1">
                    <span className={`px-2 py-0.5 text-[10px] font-mono font-medium rounded border uppercase tracking-wider ${getStatusColor(viewingCustomer.status)}`}>
                      {viewingCustomer.status.replace('_', ' ')}
                    </span>
                    <span className="text-sm font-mono text-lime-400">{viewingCustomer.loyaltyPoints} points</span>
                  </div>
                </div>
                <button
                  onClick={() => setViewingCustomer(null)}
                  className="text-[#6b8772] hover:text-[#e8f5e9] p-2 hover:bg-[#1c2822] rounded-lg transition-colors"
                >
                  <SafeIcon icon={FiX} className="w-5 h-5" />
                </button>
              </div>

              {/* Customer Stats */}
              <div className="p-5 border-b border-[#243129]">
                <div className="grid grid-cols-2 gap-4">
                  <div className="bg-[#0a0f0d] border border-[#1c2822] rounded-lg p-4">
                    <p className="text-xs font-mono text-[#6b8772] uppercase tracking-wider">Total Spent</p>
                    <p className="text-xl font-display font-semibold text-lime-400 mt-1">N$ {viewingCustomer.totalSpent.toLocaleString()}</p>
                  </div>
                  <div className="bg-[#0a0f0d] border border-[#1c2822] rounded-lg p-4">
                    <p className="text-xs font-mono text-[#6b8772] uppercase tracking-wider">Last Order</p>
                    <p className="text-xl font-display font-semibold text-[#e8f5e9] mt-1">{viewingCustomer.lastOrder}</p>
                  </div>
                </div>
              </div>

              {/* Order History */}
              <div className="p-5">
                <h3 className="text-sm font-mono font-medium text-[#a5c4ad] mb-4">Order History</h3>
                <div className="space-y-3">
                  {viewingCustomer.orderHistory.map((order, index) => (
                    <motion.div
                      key={order.id}
                      initial={{ opacity: 0, y: 10 }}
                      animate={{ opacity: 1, y: 0 }}
                      transition={{ delay: index * 0.05 }}
                      className="bg-[#0a0f0d] border border-[#1c2822] rounded-lg p-4"
                    >
                      <div className="flex items-center justify-between mb-2">
                        <div className="flex items-center gap-3">
                          <span className="font-mono font-medium text-[#e8f5e9]">{order.id}</span>
                          <span className={`px-2 py-0.5 text-[10px] font-mono font-medium rounded border uppercase tracking-wider ${
                            order.status === 'delivered' ? 'bg-lime-500/10 text-lime-400 border-lime-500/30' : 'bg-sky-500/10 text-sky-400 border-sky-500/30'
                          }`}>
                            {order.status}
                          </span>
                        </div>
                        <span className="text-lg font-display font-semibold text-[#e8f5e9]">N$ {order.total.toLocaleString()}</span>
                      </div>
                      <div className="flex items-center justify-between text-sm font-mono text-[#6b8772]">
                        <span>{order.items.join(', ')}</span>
                        <span>{order.date}</span>
                      </div>
                    </motion.div>
                  ))}
                </div>
              </div>

              {/* Modal Footer */}
              <div className="p-5 border-t border-[#243129]">
                <button
                  onClick={() => setViewingCustomer(null)}
                  className="w-full bg-[#1c2822] border border-[#243129] text-[#a5c4ad] px-4 py-3 rounded-lg hover:border-[#84cc16]/30 transition-colors font-mono text-sm font-medium"
                >
                  Close
                </button>
              </div>
            </motion.div>
          </motion.div>
        )}
      </AnimatePresence>
    </div>
  );
};

export default Sales;
